import { Button, Icon, VerticalTabs } from '@softwaredev_cegeka/stix-core'
import { Form } from '@softwaredev_cegeka/stix-form'
import axios from 'axios'
import React, { Component } from 'react'
import { Redirect } from 'react-router-dom'
import * as yup from 'yup'
import FormLayout from '../../components/Forms/FormLayout'
import { notifierStore } from '../../stores'

import './baseForm.scss'

// If the name of a field ends with a decimal surrounded by [] it's an array field.
// For instance: "hostname[0]", "foobar[3]", ...
// Array fields are typically rendered inside a <Repeater> component.
const ARRAY_FIELD_REGEX = /\[(\d)+]$/

/**
 * Maps an element name to a state key.
 * These are identical, except for ArrayFields which have names ending in [<int>]
 * @param {string} name
 * @returns {string}
 */
function nameToStateKey(name) {
    return name.replace(ARRAY_FIELD_REGEX, '')
}

/**
 * Check if the passed name is the name of an ArrayField
 * ArrayFields are stored in state as arrays.
 * @param name
 * @returns {boolean}
 */
function isArrayField(name) {
    return ARRAY_FIELD_REGEX.test(name)
}

/**
 * Retrieve the index from the element name.
 * @param name
 * @returns {number}
 */
function getArrayFieldIndex(name) {
    const matches = name.match(ARRAY_FIELD_REGEX)
    if (matches && matches.length > 1) {
        return parseInt(matches[1])
    }
}

/**
 * Base class for Order forms
 */
class BaseForm extends Component {
    static defaultProps = {
        validateOnBlur: true,
        product: {},
        // Url to redirect after submit
        redirectUrl: '',
        // Params needed for redirecting to specific pages
        redirectParams: {},
        // Url the form needs to submit to.
        submitUrl: '',
        // Method for posting data to API
        method: 'POST',
    }

    initialValues = {}

    /**
     * Implement this in your own form. Search for classes extending this one for examples.
     * @type {Array}
     */
    pages = []

    /**
     * @type {{touched: {}, values: {virpPriority: null, parentChangeOrder: string, configuration: null, virp: null, cmdbEnvironment: null, location: null, availabilityTier: null, workorder: string, customer: null, externalReferenceCegeka: string}, activePageNumber: number, errors: {}, isSubmitting: boolean}}
     */
    state = {
        // The currently active page index
        activePageNumber: 0,
        // Sets the vertical tab width
        columnSizes: { md: 3 },
        // Validation messages by name
        errors: {},
        // Whether the form is submitting or not
        isSubmitting: false,
        submittedSuccessfully: false,
        // This is used to track which fields were touched by the user.
        // If a field was touched, its validation status will be shown.
        touched: {},
        // Form values
        values: {},
        uploadProgress: null,
        createdEntityId: null,
    }

    /**
     * @returns {{renderMethod, validationSchema, page, title}|{renderMethod, title}|*}
     */
    get activePage() {
        return this.pages[this.state.activePageNumber]
    }

    /**
     * @param pageNumber
     */
    setActivePageNumber = pageNumber => {
        this.setState({ activePageNumber: parseInt(pageNumber) })
    }

    /**
     * Validate the entire form.
     * @returns {Promise<boolean>}
     */
    async validateForm() {
        let combinedSchema = this.pages.reduce(
            (schema, { validationSchema }) => {
                if (schema === null && validationSchema) {
                    return validationSchema.clone()
                }
                return validationSchema
                    ? schema.concat(validationSchema)
                    : schema
            },
            null
        )

        try {
            await combinedSchema.validate(this.state.values, {
                abortEarly: false,
            })
            return true
        } catch (err) {
            console.error(err)
            let errors = {}
            let touched = { ...this.state.touched }
            err.inner.forEach(error => {
                // get errors, including inner fields errors
                const path = error.path.split('.')[0]
                if (!errors[path]) {
                    errors[path] = []
                }
                errors[path].push(error.message)
                touched[error.path] = true
            })

            this.setState({
                errors,
                touched,
            })

            return false
        }
    }

    /**
     * Validate a whole page.
     * @param page
     * @returns {Promise<boolean>}
     */
    async validatePage(page) {
        const { validationSchema } = page

        if (!validationSchema) {
            return true
        }

        try {
            await validationSchema.validate(this.state.values, {
                abortEarly: false,
            })

            return true
        } catch (err) {
            let touched = { ...this.state.touched }
            let errors = {}
            err.inner.forEach(error => {
                errors[error.path] = error.message
                touched[error.path] = true
            })

            this.setState({
                errors,
                touched,
            })

            return false
        }
    }

    /**
     * Validate a single field.
     * @param name
     * @returns {Promise<boolean>}
     */
    async validateField(name) {
        const { validationSchema } = this.activePage
        const stateKey = nameToStateKey(name)

        if (!validationSchema || !name || !validationSchema.fields[stateKey]) {
            return true
        }

        try {
            let valueToValidate = this.state.values[stateKey]

            // For an array field we only want to validate the changed index.
            if (isArrayField(name)) {
                valueToValidate = valueToValidate[getArrayFieldIndex(name)]
            }

            await yup.reach(validationSchema, name).validate(valueToValidate)

            this.setState({
                errors: {
                    ...this.state.errors,
                    [name]: false,
                },
            })

            return true
        } catch (err) {
            console.error(err)
            this.setState({
                errors: { ...this.state.errors, [name]: err.message },
            })
        }

        return false
    }

    /**
     * @param e
     */
    handleChange = async e => {
        const { name, value, type } = e.target
        await this.setValue(
            name,
            type === 'number' && value !== '' ? Number(value) : value
        )

        // In case we had an error, revalidate on change.
        if (this.state.errors[name]) {
            this.validateField(name)
        }
    }

    /**
     * @param e
     */
    handleCheckboxChange = async e => {
        const { name, checked } = e.target
        await this.setValue(name, checked)

        // In case we had an error, revalidate on change.
        if (this.state.errors[name]) {
            this.validateField(name)
        }
    }

    /**
     * @param value
     * @param event
     */
    handleSelectChange = async (value, event) => {
        await this.setValue(event.name, value)

        // In case we had an error, revalidate on change.
        if (this.state.errors[event.name]) {
            this.validateField(event.name)
        }
    }

    /**
     * @param name
     */
    handleSelectBlur = name => {
        this._handleBlur(name)
    }

    /**
     * @param e
     */
    handleInputBlur = e => {
        this._handleBlur(e.target.name)
    }

    /**
     * @param name
     * @private
     */
    _handleBlur = name => {
        if (this.props.validateOnBlur) {
            this.validateField(name)
        }

        if (this.state.touched[name]) {
            return
        }

        this.setState({
            touched: { ...this.state.touched, [name]: true },
        })
    }

    handleUploadProgress = progressEvent => {
        this.setState({
            uploadProgress: Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
            ),
        })
    }

    /**
     * @param e
     */
    handleSubmit = async e => {
        e.preventDefault()
        let isValid = await this.validateForm()

        if (isValid) {
            this.setState({ isSubmitting: true })
            try {
                await this.submitData()
            } catch (err) {
                console.error(err)
                // TODO: error handling
            } finally {
                this.setState({ isSubmitting: false })
            }
        } else {
            this.activateFirstPageWithErrors()
        }
    }

    getSubmitUrl() {
        return null
    }

    getSubmitMethod() {
        return null
    }

    getSuccessMessage() {
        return 'Submitted the data you provided successfully'
    }

    getFailureMessage() {
        return 'Could not submit the data you provided'
    }

    submitData = async () => {
        const payload = this.generatePayload()
        const { method } = this.props
        if (!payload) {
            console.warn('No data to submit')
            console.info('state values: ', this.state.values)
            return
        }
        try {
            let resp = await axios({
                url: this.getSubmitUrl() || this.props.submitUrl,
                method: this.getSubmitMethod() || method,
                data: payload,
                onUploadProgress: this.handleUploadProgress,
                timeout: this.props.timeout,
            })
            if (resp.status === 200) {
                notifierStore.setToastInfo(this.getSuccessMessage(), true)
                this.setState({
                    createdEntityId: resp.data.id,
                    submittedSuccessfully: true,
                })
            } else {
                notifierStore.setToastInfo(this.getFailureMessage(), false)
            }
        } catch (ex) {
            console.warn('API Exception', ex)
            notifierStore.setToastInfo(
                ex.response?.data?.error || ex.response?.data?.detail,
                false
            )
        } finally {
            notifierStore.setShowMessage(true)
        }
    }

    /**
     * Implement this in your subclass
     */
    generatePayload = () => {
        console.warn(
            'You did not implement a generatePayload method in your CcpForm. Cannot submit form without it'
        )
        return null
    }

    /**
     * @param name
     * @returns {*}
     */
    getValue(name) {
        if (isArrayField(name)) {
            return this.state.values[nameToStateKey(name)]?.[
                getArrayFieldIndex(name)
            ]
        }

        return this.state.values[name]
    }

    /**
     * @param name
     * @param value
     */
    async setValue(name, value) {
        // if the name is of a Repeated element, only update the changed index
        if (this.getValue(name) === value) {
            return
        }

        // return a promise in case someone wants to know when the actual state update was done
        return new Promise(resolve => {
            this.setState(
                (state, props) => {
                    let newValue = value
                    let stateKey = name

                    if (isArrayField(name)) {
                        stateKey = nameToStateKey(name)
                        newValue = [...this.state.values[stateKey]]
                        newValue[getArrayFieldIndex(name)] = value
                    }

                    return {
                        values: {
                            ...state.values,
                            [stateKey]: newValue,
                        },
                    }
                },
                () => resolve()
            )
        })
    }

    /**
     * @param name
     * @returns {{onBlur: BaseForm.handleInputBlur, onChange: BaseForm.handleChange, name: *, value: *}}
     */
    getCommonInputProps(name) {
        const { errors, touched } = this.state

        let props = {
            name,
            onChange: this.handleChange,
            onBlur: this.handleInputBlur,
            value: this.getValue(name),
        }
        if (touched[name]) {
            props.isValid = !errors[name]
            props.isInvalid = !!errors[name]

            if (props.isInvalid) {
                // We should add array support to feedback.
                props.feedback = errors[name]
            }
        }

        return props
    }

    /**
     * @param name
     * @returns {Object}
     */
    getCommonSelectProps(name) {
        let props = this.getCommonInputProps(name)
        const { errors } = this.state
        if (props.isInvalid && errors[name]?.id) {
            props.feedback = errors[name].id
        }

        props.onChange = this.handleSelectChange
        props.onBlur = () => this.handleSelectBlur(name)

        return props
    }

    /**
     * @param name
     * @returns {Object}
     */
    getCommonCheckboxProps(name) {
        let props = this.getCommonInputProps(name)
        props.checked = props.value
        props.onChange = this.handleCheckboxChange
        delete props.value
        return props
    }

    /**
     * @param name
     * @param value
     * @returns {Object}
     */
    getCommonRadioProps(name, value) {
        let props = this.getCommonInputProps(name)
        props.custom = true
        props.value = value
        props.checked = this.state.values[name] === value
        return props
    }

    /**
     * @returns {Promise<void>}
     */
    activateNextPage = async () => {
        let isValid = await this.validatePage(this.activePage)
        if (isValid && this.pages[this.state.activePageNumber + 1]) {
            this.setActivePageNumber(this.state.activePageNumber + 1)
        }
    }

    /**
     * Go to the first page that contains an error
     */
    activateFirstPageWithErrors = () => {
        let pageNumber = this.pages.findIndex(page => {
            return Object.keys(page.validationSchema.fields).some(name => {
                return !!this.state.errors[name]
            })
        })

        this.setActivePageNumber(pageNumber)
    }

    /**
     * @param pageNumber
     * @returns {*}
     */
    renderFormActions(pageNumber) {
        return (
            <div className="ccp-form-actions">
                {this.pages.length > 1 ? (
                    <Button
                        variant="light"
                        disabled={pageNumber === 0}
                        onClick={() => {
                            this.setActivePageNumber(pageNumber - 1)
                        }}
                    >
                        Previous page
                    </Button>
                ) : null}
                {pageNumber + 1 !== this.pages.length ? (
                    <Button variant="secondary" onClick={this.activateNextPage}>
                        Next page
                    </Button>
                ) : (
                    <Button
                        key="submit"
                        variant="secondary"
                        type="submit"
                        disabled={this.state.isSubmitting}
                    >
                        {this.state.isSubmitting ? (
                            <Icon
                                className="animate-spin"
                                icon={'loading'}
                                title={'loading'}
                            />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                )}
            </div>
        )
    }

    /**
     * @returns {*}
     */
    render() {
        if (this.state.submittedSuccessfully) {
            const { redirectUrl } = this.props
            return <Redirect push to={redirectUrl} />
        }
        return (
            <FormLayout {...this.props}>
                <Form onSubmit={this.handleSubmit} noValidate>
                    {this.pages.length > 1 ? (
                        <VerticalTabs
                            activeKey={this.state.activePageNumber}
                            onSelect={this.setActivePageNumber}
                            tabColumnSizes={this.state.columnSizes}
                        >
                            {this.pages.map((page, i) => {
                                return (
                                    <VerticalTabs.Pane
                                        key={page.title}
                                        eventKey={i}
                                        title={page.title}
                                    >
                                        {page.renderMethod.call(this)}
                                        {this.renderFormActions(i)}
                                    </VerticalTabs.Pane>
                                )
                            })}
                        </VerticalTabs>
                    ) : (
                        <>
                            {this.pages[0].renderMethod.call(this)}
                            {this.renderFormActions(0)}
                        </>
                    )}
                </Form>
            </FormLayout>
        )
    }
}

export default BaseForm
